import { MoveLeft, MoveRight } from "lucide-react";
import { useEffect, useState } from "react";
import { BottomBar } from "@/components/shared/BottomBar";
import Faq1 from "@stackje/shared-components/cards/faq/Faq1";
import Faq2 from "@stackje/shared-components/cards/faq/Faq2";
import Feature1 from "@stackje/shared-components/cards/feature/Feature1";
import Feature2 from "@stackje/shared-components/cards/feature/Feature2";
import Hero1 from "@stackje/shared-components/cards/hero/Hero1";
import Hero2 from "@stackje/shared-components/cards/hero/Hero2";
import Hero3 from "@stackje/shared-components/cards/hero/Hero3";
import Hero4 from "@stackje/shared-components/cards/hero/Hero4";
import Hero5 from "@stackje/shared-components/cards/hero/Hero5";
import Link1 from "@stackje/shared-components/cards/link/Link1";
import Link2 from "@stackje/shared-components/cards/link/Link2";
import Link5 from "@stackje/shared-components/cards/link/Link5";
import Link6 from "@stackje/shared-components/cards/link/Link6";
import Image3 from "@stackje/shared-components/cards/image/Image3";
import Image4 from "@stackje/shared-components/cards/image/Image4";
import Image5 from "@stackje/shared-components/cards/image/Image5";
import Text1 from "@stackje/shared-components/cards/text/Text1";
import Whatsapp1 from "@stackje/shared-components/cards/whatsapp/Whatsapp1";
import Whatsapp2 from "@stackje/shared-components/cards/whatsapp/Whatsapp2";
import Tipping1 from "@stackje/shared-components/cards/tipping/Tipping1";
// import Tipping2 from "@stackje/shared-components/cards/tipping/Tipping2"; // Disabled for now
import { Button } from "@stackje/shared-components/ui/button";
import {
  Carousel,
  type CarouselApi,
  CarouselContent,
  CarouselItem,
} from "@stackje/shared-components/ui/carousel";
import { ScaledPreviewContainer } from "@stackje/shared-components/ui/scaled-preview-container";
import { sampleData } from "@/components/add-card/constants";
import { type CommonColor } from "@stackje/common/config";

interface CardTypeSelectorProps {
  cardType: string;
  onSelect: (subtype: string) => void;
  colors: CommonColor;
}

export function CardTypeSelector({
  cardType,
  onSelect,
  colors,
}: CardTypeSelectorProps) {
  const getCardTypes = () => {
    switch (cardType) {
      case "hero":
        return [
          { type: "hero1", component: <Hero1 {...sampleData.hero1} colors={colors} /> },
          { type: "hero2", component: <Hero2 {...sampleData.hero2} colors={colors} /> },
          { type: "hero3", component: <Hero3 {...sampleData.hero3} colors={colors} /> },
          { type: "hero4", component: <Hero4 {...sampleData.hero4} colors={colors} /> },
          { type: "hero5", component: <Hero5 {...sampleData.hero5} colors={colors} /> },
        ];
      case "text":
        return [
          {
            type: "text1",
            component: (
              <Text1
                title={sampleData.text.title}
                subtitle={sampleData.text.subtitle}
                colors={colors}
              />
            ),
          },
        ];
      case "links":
        return [
          { type: "link1", component: <Link1 {...sampleData.links} colors={colors} /> },
          { type: "link2", component: <Link2 {...sampleData.links} colors={colors} /> },
          {
            type: "link5",
            component: <Link5 {...sampleData.links} colors={colors} />,
          },
          {
            type: "link6",
            component: <Link6 {...sampleData.links} colors={colors} />,
          },
        ];
      case "images":
        return [
          // { type: "image1", component: <Image1 {...sampleData.images} colors={currentColors} /> },
          // { type: "image2", component: <Image2 {...sampleData.images} colors={currentColors} /> },
          { type: "image3", component: <Image3 {...sampleData.images} colors={colors} /> },
          { type: "image4", component: <Image4 {...sampleData.image4} colors={colors} /> },
          { type: "image5", component: <Image5 {...sampleData.image5} colors={colors} /> },
        ];
      case "features":
        return [
          {
            type: "feature1",
            component: (
              <Feature1
                title={sampleData.features.title}
                subtitle={sampleData.features.subtitle}
                features={sampleData.features.features}
                colors={colors}
              />
            ),
          },
          {
            type: "feature2",
            component: (
              <Feature2
                title={sampleData.features.title}
                subtitle={sampleData.features.subtitle}
                features={sampleData.features.features}
                colors={colors}
              />
            ),
          },
        ];
      case "faq":
        return [
          {
            type: "faq1",
            component: (
              <Faq1
                title={sampleData.faq.title}
                description={sampleData.faq.description}
                faqs={sampleData.faq.faqs}
                colors={colors}
              />
            ),
          },
          {
            type: "faq2",
            component: (
              <Faq2
                title={sampleData.faq2.title}
                description={sampleData.faq2.description}
                faqs={sampleData.faq2.faqs}
                colors={colors}
              />
            ),
          },
        ];
      case "whatsapp":
        return [
          { type: "whatsapp1", component: <Whatsapp1 {...sampleData.whatsapp} colors={colors} /> },
          { type: "whatsapp2", component: <Whatsapp2 {...sampleData.whatsapp} colors={colors} /> },
        ];
      case "tipping":
        return [
          { type: "tipping1", component: <Tipping1 {...sampleData.tipping1} colors={colors} /> },
          // { type: "tipping2", component: <Tipping2 {...sampleData.tipping2} colors={colors} /> }, // Disabled for now
        ];
      default:
        return [];
    }
  };

  const cardTypes = getCardTypes();
  const [api, setApi] = useState<CarouselApi>();
  const [currentIndex, setCurrentIndex] = useState(0);
  const [count, setCount] = useState(0);

  useEffect(() => {
    if (!api) {
      return;
    }

    setCurrentIndex(api.selectedScrollSnap());
    setCount(api.scrollSnapList().length);

    api.on("select", () => {
      setCurrentIndex(api.selectedScrollSnap());
    });
  }, [api]);

  return (
    <div className="w-full mt-4 py-0">
      <Carousel
        setApi={setApi}
        className="w-full mx-auto"
        opts={{
          align: "start",
          loop: true,
        }}
      >
        <CarouselContent>
          {cardTypes.map((card, index) => (
            <CarouselItem key={index} className="flex justify-center align-middle pb-0 mb-0">
              <ScaledPreviewContainer>
                {card.component}
              </ScaledPreviewContainer>
            </CarouselItem>
          ))}
        </CarouselContent>
        <BottomBar>
          {count > 1 && (
            <Button
              variant="ghost"
              aria-label="Previous card"
              onClick={() => {
                api?.scrollPrev();
              }}
            >
              <MoveLeft />
            </Button>
          )}
          <Button
            className="px-12 mx-auto"
            onClick={() => {
              onSelect(cardTypes[currentIndex].type);
            }}
          >
            Select Style
          </Button>
          {count > 1 && (
            <Button
              variant="ghost"
              aria-label="Next card"
              onClick={() => {
                api?.scrollNext();
              }}
            >
              <MoveRight />
            </Button>
          )}
        </BottomBar>
      </Carousel>
    </div>
  );
}
